// ============================================================
// Primo — calculation engine (single source of truth)
// ============================================================
//
// The trailer has TWO frames per set:
//   Frame A (left):  doorsealing on 3 edges (5.0m total) + centre profile on the inner edge (2.6m).
//                    Total profile cost = DS_price × 5.0 + CP_price × 2.6.
//   Frame B (right): doorsealing on ALL 4 edges (7.6m total). No centre profile — Frame B seals
//                    against Frame A's centre profile.  Total profile cost = DS_price × 7.6.
//
// Per-meter prices are the same on both frames (5€/m customer, 7€/m Primo for doorsealing;
// 6€/m customer, 8.5€/m Primo for centre profile).  The asymmetry is in the LENGTHS and in
// which profile is present, not in the €/m prices.
//
// IMPORTANT MODELLING RULES (matches the original Primo PDF):
//   • Labour costs (cutting/gluing/mounting) are PER FRAME → multiply by doors_per_trailer
//     for the total on a set.
//   • Steel band + drilling + rivets are PER FRAME → multiply by doors_per_trailer.
//   • Glue/materials are PER FRAME (€12/frame allocation → €24 for 2 frames).
//   • Times are stored internally in MINUTES and only converted to hours in the UI.
//   • Welding & cutting time = 25 min (NOT "4" — the 4 refers to 4 corners).
//   • Nothing is hardcoded: every total is derived from the input fields.
// ============================================================

// PDF-derived lengths (metres) used to convert €/m into per-frame cost
export const FRAME_A_DS_LEN_M = 5.0;
export const FRAME_A_CP_LEN_M = 2.6;
export const FRAME_B_DS_LEN_M = 7.6;
export const FRAME_B_CP_LEN_M = 0.0;

// Welding is performed on 4 corners of each frame (informational — used in labels only)
export const WELDING_CORNERS = 4;

// Multiplier from selected period → annual (rough approximation)
export const PERIOD_TO_ANNUAL = {
  weekly: 52,
  monthly: 12,
  annual: 1,
};

export const defaultTrailer = {
  height_mm: 2600,
  width_mm: 1200,
  doors_per_trailer: 2,
  fleet_size: 1,
  annual_production: 50,
  production_period: "weekly", // "weekly" | "monthly" | "annual"
  sides_differ: true,
  dimensions_differ: false,
  right_height_mm: 2600,
  right_width_mm: 1200,
  // Single hourly labour rate (€/h). Applied to EVERY labour operation in Phase 2 and
  // Phase 3 so that `cost = (time_min / 60) × labor_hourly_rate` is always consistent.
  labor_hourly_rate: 12.0,
  // Profile thickness (mm) from selected preset — used for dynamic labels.
  profile_thickness_mm: 85,
};

export const defaultCustomer = {
  // Frame A (left) — doorsealing + centre profile
  doorsealing_price_per_m: 5.0,
  centerprofile_price_per_m: 6.0,
  // Frame B (right) — doorsealing only
  right_doorsealing_price_per_m: 5.0,
  right_centerprofile_price_per_m: 0.0,
  // Labour (per frame)
  cutting_time_min: 40.0,
  cutting_cost: 8.0,
  corner_gluing_time_min: 60.0,
  corner_gluing_cost: 12.0,
  mounting_time_min: 360.0,
  mounting_cost: 72.0,
  // Steel band hardware (per frame).
  // Per-metre model: total steel-band cost per frame =
  //   steelband_price_per_m × (steelband_length_a_m + steelband_length_b_m)
  // Baseline: 6 €/m × (7.6 + 7.6) m = 91.20 € per frame — matches the PDF.
  steelband_price_per_m: 6.0,
  steelband_length_a_m: 7.6,
  steelband_length_b_m: 7.6,
  // Legacy flat price — 0 for new sims. Simulations saved BEFORE the per-metre
  // model existed carry a non-zero value here and no `steelband_price_per_m`,
  // in which case `customerBreakdown` falls back to the flat value.
  steelband_price: 0.0,
  // Drilling stored as TIME (min/frame), converted to € via `hourlyRate` in
  // customerBreakdown(). Default 38 min at 12 €/h → 7.60 € per frame.
  drilling_time_min: 38.0,
  popniets_cost: 1.5,
};

export const defaultPrimo = {
  // Frame A (left) — doorsealing + centre profile
  doorsealing_price_per_m: 7.0,
  centerprofile_price_per_m: 8.5,
  // Frame B (right) — doorsealing only
  right_doorsealing_price_per_m: 7.0,
  right_centerprofile_price_per_m: 0.0,
  // Labour (per frame). 4 corners × time per corner → welding & cutting = 25 min per frame.
  welding_cutting_time_min: 25.0,
  welding_cutting_cost: 5.0,
  gluing_time_min: 25.0,
  gluing_cost: 5.0,
  mounting_time_min: 60.0,
  mounting_cost: 12.0,
  // Materials — allocated glue cost per frame (per PDF: €12/frame → €24 total for 2 frames)
  glue_cost: 12.0,
};

export const defaultClient = {
  company_name: "",
  contact_name: "",
  contact_email: "",
  notes: "",
};

// ============================================================
// Derived breakdown helpers
// ============================================================

function frameProfileCost(dsPrice, cpPrice, dsLenM, cpLenM) {
  return dsPrice * dsLenM + cpPrice * cpLenM;
}

function n(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

/**
 * Compute the physical lengths (in metres) of the doorsealing + centre profile
 * for a rear-door of `heightMm × widthMm`.
 *
 * Layout (matches the PDF baseline for a 2600×1200 door):
 *   • Frame A (left, 3 outer edges + centre): DS = 2·W + H  ·  CP = H
 *   • Frame B (right, full perimeter):        DS = 2·(H + W) · CP = 0
 *
 * Everything is derived from the door dimensions so the model reacts to
 * any change of altura / largura on Phase 1.
 */
export function computeFrameLengths(heightMm, widthMm) {
  const H = (Number(heightMm) || 0) / 1000;
  const W = (Number(widthMm) || 0) / 1000;
  return {
    frameA_ds: 2 * W + H,
    frameA_cp: H,
    frameB_ds: 2 * (H + W),
    frameB_cp: 0,
  };
}

/**
 * Customer breakdown for a full set of `doors` frames.
 * Labour cost = (time_min / 60) × hourlyRate, applied per operation.
 * `trailer` (with height_mm, width_mm, right_height_mm, right_width_mm,
 * dimensions_differ) is required so lengths are computed dynamically.
 */
export function customerBreakdown(c, sidesDiffer = false, doors = 2, hourlyRate = 12, trailer = {}) {
  const doorsCount = Math.max(1, Math.round(Number(doors) || 2));
  const rate = Math.max(0, Number(hourlyRate) || 0);

  // Frame lengths — Frame A uses (left) door dims; Frame B uses right door dims
  // when dimensions_differ is true, otherwise the same left dims.
  const lenA = computeFrameLengths(trailer.height_mm, trailer.width_mm);
  const lenB = trailer.dimensions_differ
    ? computeFrameLengths(trailer.right_height_mm, trailer.right_width_mm)
    : lenA;

  const leftDs = n(c.doorsealing_price_per_m);

  let frameAProfiles, frameBProfiles;

  if (sidesDiffer) {
    // Asymmetric: left door has DS+CP, right door has DS only
    const rightDs = n(c.right_doorsealing_price_per_m);
    const leftCp = n(c.centerprofile_price_per_m);
    const rightCp = n(c.right_centerprofile_price_per_m);
    frameAProfiles = frameProfileCost(leftDs, leftCp, lenA.frameA_ds, lenA.frameA_cp);
    frameBProfiles = frameProfileCost(rightDs, rightCp, lenB.frameB_ds, lenB.frameB_cp);
  } else {
    // Symmetric: ALL doors identical — full perimeter doorsealing, NO centre profile
    const fullPerimDs = lenA.frameB_ds; // 2*(H+W) = full perimeter
    const costPerDoor = leftDs * fullPerimDs; // DS only, no CP
    frameAProfiles = costPerDoor;
    frameBProfiles = costPerDoor;
  }

  // Labour costs are derived from time × hourly rate — never entered directly.
  const cuttingCost = (n(c.cutting_time_min) / 60) * rate;
  const gluingCost = (n(c.corner_gluing_time_min) / 60) * rate;
  const mountingCost = (n(c.mounting_time_min) / 60) * rate;
  const laborPerFrame = cuttingCost + gluingCost + mountingCost;

  const steelbandLengthTotalM = n(c.steelband_length_a_m) + n(c.steelband_length_b_m);
  const steelbandPricePerM = n(c.steelband_price_per_m);
  // Per-metre model when available; legacy flat price when not (backwards compat
  // for sims saved before the per-metre model existed).
  const steelbandBaseCost = steelbandPricePerM > 0
    ? steelbandPricePerM * steelbandLengthTotalM
    : n(c.steelband_price);
  // Drilling is stored as TIME (min/frame) and converted to €
  // using the same labor hourly rate as the other operations.
  const drillingCost = (n(c.drilling_time_min) / 60) * rate;
  const steelbandPerFrame = steelbandBaseCost + drillingCost + n(c.popniets_cost);

  const frameATotal = frameAProfiles + laborPerFrame + steelbandPerFrame;
  const frameBTotal = frameBProfiles + laborPerFrame + steelbandPerFrame;

  const profiles = sidesDiffer && doorsCount === 2
    ? frameAProfiles + frameBProfiles
    : (frameAProfiles + frameBProfiles) * (doorsCount / 2);

  const labor = laborPerFrame * doorsCount;
  const steelband = steelbandPerFrame * doorsCount;
  const materials = 0;
  const total = profiles + labor + steelband;

  return {
    frameAProfiles,
    frameBProfiles,
    laborPerFrame,
    steelbandPerFrame,
    steelbandBaseCost,      // steel-band per-metre (or legacy flat) cost, EXCLUDING drilling + rivets
    steelbandPricePerM,     // €/m entered by the user (0 if legacy flat mode)
    steelbandLengthTotalM,  // length A + length B, in metres
    drillingCost,           // drilling cost per frame in € (time × rate)
    materialsPerFrame: 0,
    frameATotal,
    frameBTotal,
    cuttingCost,
    gluingCost,
    mountingCost,
    profiles,
    labor,
    steelband,
    materials,
    total,
    doors: doorsCount,
    hourlyRate: rate,
    lengthsA: lenA,
    lengthsB: lenB,
  };
}

/**
 * Primo breakdown for a full set of `doors` frames.
 * Labour cost = (time_min / 60) × hourlyRate, applied per operation.
 */
export function primoBreakdown(p, sidesDiffer = false, doors = 2, hourlyRate = 12, trailer = {}) {
  const doorsCount = Math.max(1, Math.round(Number(doors) || 2));
  const rate = Math.max(0, Number(hourlyRate) || 0);

  const lenA = computeFrameLengths(trailer.height_mm, trailer.width_mm);
  const lenB = trailer.dimensions_differ
    ? computeFrameLengths(trailer.right_height_mm, trailer.right_width_mm)
    : lenA;

  const leftDs = n(p.doorsealing_price_per_m);

  let frameAProfiles, frameBProfiles;

  if (sidesDiffer) {
    const rightDs = n(p.right_doorsealing_price_per_m);
    const leftCp = n(p.centerprofile_price_per_m);
    const rightCp = n(p.right_centerprofile_price_per_m);
    frameAProfiles = frameProfileCost(leftDs, leftCp, lenA.frameA_ds, lenA.frameA_cp);
    frameBProfiles = frameProfileCost(rightDs, rightCp, lenB.frameB_ds, lenB.frameB_cp);
  } else {
    // Symmetric: ALL doors identical — full perimeter doorsealing, NO centre profile
    const fullPerimDs = lenA.frameB_ds; // 2*(H+W) = full perimeter
    const costPerDoor = leftDs * fullPerimDs;
    frameAProfiles = costPerDoor;
    frameBProfiles = costPerDoor;
  }

  const weldingCost = (n(p.welding_cutting_time_min) / 60) * rate;
  const gluingCost = (n(p.gluing_time_min) / 60) * rate;
  const mountingCost = (n(p.mounting_time_min) / 60) * rate;
  const laborPerFrame = weldingCost + gluingCost + mountingCost;

  const materialsPerFrame = n(p.glue_cost);

  const frameATotal = frameAProfiles + laborPerFrame + materialsPerFrame;
  const frameBTotal = frameBProfiles + laborPerFrame + materialsPerFrame;

  const profiles = sidesDiffer && doorsCount === 2
    ? frameAProfiles + frameBProfiles
    : (frameAProfiles + frameBProfiles) * (doorsCount / 2);

  const labor = laborPerFrame * doorsCount;
  const materials = materialsPerFrame * doorsCount;
  const steelband = 0;
  const total = profiles + labor + materials;

  return {
    frameAProfiles,
    frameBProfiles,
    laborPerFrame,
    materialsPerFrame,
    steelbandPerFrame: 0,
    frameATotal,
    frameBTotal,
    // per-operation labour costs (per frame)
    weldingCost,
    gluingCost,
    mountingCost,
    profiles,
    labor,
    materials,
    steelband,
    total,
    doors: doorsCount,
    hourlyRate: rate,
    lengthsA: lenA,
    lengthsB: lenB,
  };
}

// ============================================================
// Time breakdowns — internally stored & returned in MINUTES.
// Consumers convert to hours only at display time.
// ============================================================

/**
 * Customer time breakdown.
 * Returns per-operation per-frame minutes plus set totals (× doors).
 */
export function customerTimeBreakdown(c, doors = 2) {
  const doorsCount = Math.max(1, Math.round(Number(doors) || 2));
  const cutting = n(c.cutting_time_min);
  const gluing = n(c.corner_gluing_time_min);
  const mounting = n(c.mounting_time_min);
  const perFrame = cutting + gluing + mounting;
  const total = perFrame * doorsCount;
  return {
    cutting,
    gluing,
    mounting,
    perFrame,
    total, // minutes for the whole set of `doors` frames
    doors: doorsCount,
  };
}

/**
 * Primo time breakdown.
 * Note: `welding_cutting_time_min` = 25 min per frame (4 corners × ~6.25 min).
 */
export function primoTimeBreakdown(p, doors = 2) {
  const doorsCount = Math.max(1, Math.round(Number(doors) || 2));
  const welding = n(p.welding_cutting_time_min);
  const gluing = n(p.gluing_time_min);
  const mounting = n(p.mounting_time_min);
  const perFrame = welding + gluing + mounting;
  const total = perFrame * doorsCount;
  return {
    welding,
    gluing,
    mounting,
    perFrame,
    total, // minutes for the whole set of `doors` frames
    doors: doorsCount,
  };
}

// ============================================================
// High-level savings aggregator (used by Phase 4/5 + PDF).
// ============================================================
export function computeSavings(state) {
  const trailer = state.trailer || {};
  const sidesDiffer = !!trailer.sides_differ;
  const doors = Number(trailer.doors_per_trailer) || 2;
  const hourlyRate = Number(trailer.labor_hourly_rate) || 12;

  const cb = customerBreakdown(state.customer, sidesDiffer, doors, hourlyRate, trailer);
  const pb = primoBreakdown(state.primo, sidesDiffer, doors, hourlyRate, trailer);
  const ct = customerTimeBreakdown(state.customer, doors);
  const pt = primoTimeBreakdown(state.primo, doors);

  const priceCustomer = cb.total;
  const pricePrimo = pb.total;
  const priceSavings = Math.max(0, priceCustomer - pricePrimo);
  const priceReductionPct = priceCustomer > 0 ? (priceSavings / priceCustomer) * 100 : 0;

  // Time — always in minutes internally
  const timeCustomerMin = ct.total;
  const timePrimoMin = pt.total;
  const timeSavingsMin = Math.max(0, timeCustomerMin - timePrimoMin);
  const timeReductionPct = timeCustomerMin > 0 ? (timeSavingsMin / timeCustomerMin) * 100 : 0;

  const fleet = Math.max(1, Number(trailer.fleet_size) || 1);
  const quantity = Math.max(0, Number(trailer.annual_production) || 0);
  const period = trailer.production_period || "annual";
  const annualMult = PERIOD_TO_ANNUAL[period] || 1;

  // A "set" (2 frames) = one trailer's rear closure. Savings above already
  // represent one full set. Fleet × units per period × periods → annual.
  const periodSavings = priceSavings * quantity * fleet;
  const annualSavings = periodSavings * annualMult;
  const periodTimeMin = timeSavingsMin * quantity * fleet;
  const annualTimeMin = periodTimeMin * annualMult;

  return {
    cb, pb, ct, pt,
    doors,
    priceCustomer,
    pricePrimo,
    priceSavings,
    priceReductionPct,
    timeCustomerMin,
    timePrimoMin,
    timeSavingsMin,
    timeCustomerH: timeCustomerMin / 60,
    timePrimoH: timePrimoMin / 60,
    timeSavingsH: timeSavingsMin / 60,
    timeReductionPct,
    fleet,
    quantity,
    period,
    annualMult,
    periodSavings,
    annualSavings,
    periodTimeMin,
    annualTimeMin,
    periodTimeH: periodTimeMin / 60,
    annualTimeH: annualTimeMin / 60,
  };
}
